import { Box, Grid, IconButton } from '@material-ui/core';
import useStyles from './useStyles';
import { UploadedDrawingFileType } from '@modules/request';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { TrashCan } from '@carbon/icons-react';

import {
  ServiceTypes,
  OptionComponent,
  SelectFileDialogProps
} from './RequestForQuotationUpload';

import { useFormContext as useFormContextFromContainer } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { setTimeout } from 'timers';

interface Props extends ServiceTypes {
  uploadedDrawingFiles: UploadedDrawingFileType[];
  onSelectFile: (payload: SelectFileDialogProps) => void;
}

const TableItems = [
  { id: 'index', title: 'No.', maxWidth: '23px', marginRight: '16px' },
  { id: 'fileName', title: '파일명', maxWidth: '175px', marginRight: '24px' },
  { id: 'option', title: '가공 옵션*', maxWidth: '323px', marginRight: '24px' },
  { id: 'button', title: '', maxWidth: 'auto', marginRight: '0px' }
];

const RequestForQuotationFileTable = ({
  uploadedDrawingFiles,
  serviceType,
  detailedServiceType,
  onSelectFile
}: Props) => {
  const classes = useStyles();
  const { errors } = useFormContextFromContainer();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (Object.keys(errors).some((key) => key.includes('-'))) {
      setTimeout(() => {
        ref.current?.scrollIntoView();
      }, 1);
    }
  }, [errors]);

  return (
    <Grid container className={classes['rfq-drawingfiles-table']} ref={ref}>
      <Grid item xs={12} className="table-header">
        {TableItems.map((item, index) => (
          <Box
            key={item.title}
            className="table-header-item"
            maxWidth={item.maxWidth}
            marginRight={index !== 3 ? item.marginRight : 0}
          >
            <span>{item.title}</span>
          </Box>
        ))}
      </Grid>
      <Grid item xs={12} className="table-body">
        {uploadedDrawingFiles.map((file, index) => {
          if (!file) {
            return;
          }

          const { fileId, fileName, isValid } = file;

          const isError = Object.keys(errors).some((key) => key === fileId);

          const lastDot = fileName?.lastIndexOf('.') ?? 0;
          const length = fileName?.length ?? 0;
          const elipsisFileName =
            length > 15
              ? fileName?.slice(0, 15) + '...'
              : fileName?.slice(0, lastDot);

          const extension = fileName?.slice(lastDot + 1).toLowerCase();

          const filteredFileName = elipsisFileName + '.' + extension;

          return (
            <div className="table-data" key={index}>
              <div className="item-group">
                <Box
                  width="100%"
                  maxWidth={TableItems[0].maxWidth}
                  marginRight={TableItems[0].marginRight}
                >
                  <CustomizedTypography
                    fontWeight="normal"
                    variant="body2"
                    color="textPrimary"
                  >
                    {index + 1}
                  </CustomizedTypography>
                </Box>
                <Box
                  width="100%"
                  maxWidth={TableItems[1].maxWidth}
                  marginRight={TableItems[1].marginRight}
                >
                  <CustomizedTypography
                    fontWeight="bold"
                    variant="body2"
                    color="textPrimary"
                  >
                    {filteredFileName}
                  </CustomizedTypography>
                </Box>
                <Box
                  width="100%"
                  maxWidth={TableItems[2].maxWidth}
                  marginRight={TableItems[2].marginRight}
                >
                  {isValid ? (
                    <OptionComponent
                      {...file}
                      detailedServiceType={detailedServiceType}
                      serviceType={serviceType}
                    />
                  ) : (
                    <CustomizedTypography
                      variant="body2"
                      color={isError ? 'error' : 'textSecondary'}
                    >
                      선택한 재료 옵션이 없습니다. 재료 옵션을 선택해주세요.
                    </CustomizedTypography>
                  )}
                </Box>
              </div>
              <div className="button-group">
                <CustomizedButton
                  className="option-button"
                  variant={isValid ? 'outlined' : 'contained'}
                  color="primary"
                  size="small"
                  onClick={() => {
                    onSelectFile({ fileId: fileId, mode: 'change' });
                  }}
                >
                  {isValid ? '수량 및 재료 수정' : '수량 및 재료 입력'}
                </CustomizedButton>
                <IconButton
                  className="delete-button"
                  onClick={() => {
                    onSelectFile({ fileId: fileId, mode: 'delete' });
                  }}
                >
                  <TrashCan size={24} />
                </IconButton>
              </div>
            </div>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default RequestForQuotationFileTable;
